import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {};

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
  if (!this.#allResultsItems?.length) {
    return;
  }
    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
  if (event.key === 'Escape') {
    this.#resetSearch();
    return;
  }

  /*
   * ENTER ROUTING
   * Brand/category searches go directly to their showroom/collection.
   * Everything else uses the visible predictive result.
   */
  if (event.key === 'Enter') {
    const term = this.refs.searchInput.value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

    const directRoutes = {
      /* Categories */
      'scooter': '/collections/electric-scooters',
      'scooters': '/collections/electric-scooters',
      'electric scooter': '/collections/electric-scooters',
      'electric scooters': '/collections/electric-scooters',
      'escooter': '/collections/electric-scooters',
      'escooters': '/collections/electric-scooters',
      'e-scooter': '/collections/electric-scooters',
      'e-scooters': '/collections/electric-scooters',

      'bike': '/collections/electric-bikes',
      'bikes': '/collections/electric-bikes',
      'ebike': '/collections/electric-bikes',
      'ebikes': '/collections/electric-bikes',
      'e-bike': '/collections/electric-bikes',
      'e-bikes': '/collections/electric-bikes',
      'electric bike': '/collections/electric-bikes',
      'electric bikes': '/collections/electric-bikes',

      'dirt bike': '/collections/electric-dirt-bikes',
      'dirt bikes': '/collections/electric-dirt-bikes',
      'electric dirt bike': '/collections/electric-dirt-bikes',
      'electric dirt bikes': '/collections/electric-dirt-bikes',

      'efoil': '/collections/efoils',
      'efoils': '/collections/efoils',
      'e-foil': '/collections/efoils',
      'e-foils': '/collections/efoils',

      'jetboard': '/collections/electric-surfboards',
      'jetboards': '/collections/electric-surfboards',
      'electric surfboard': '/collections/electric-surfboards',
      'electric surfboards': '/collections/electric-surfboards',
      'surfboard': '/collections/electric-surfboards',
      'surfboards': '/collections/electric-surfboards',

      'boat platform': '/collections/boat-platforms-yachtbeach',
      'boat platforms': '/collections/boat-platforms-yachtbeach',
      'yacht platform': '/collections/boat-platforms-yachtbeach',
      'yacht platforms': '/collections/boat-platforms-yachtbeach',

      'yacht furniture': '/collections/yacht-lounge-furniture-yachtbeach',
      'lounge furniture': '/collections/yacht-lounge-furniture-yachtbeach',
      'yacht lounge furniture': '/collections/yacht-lounge-furniture-yachtbeach',

      'water toy dock': '/collections/water-toy-docks-yachtbeach',
      'water toy docks': '/collections/water-toy-docks-yachtbeach',

      'yacht slide': '/collections/yacht-slides-yachtbeach',
      'yacht slides': '/collections/yacht-slides-yachtbeach',

      'boarding equipment': '/collections/boarding-equipment-yachtbeach',
      'boarding ladder': '/collections/boarding-equipment-yachtbeach',
      'boarding ladders': '/collections/boarding-equipment-yachtbeach',

      /* Brands */
      'freego': '/collections/freego',
      'heybike': '/collections/heybike',
      'hey bike': '/collections/heybike',
      'ghostcat': '/collections/ghostcat',
      'ghost cat': '/collections/ghostcat',
      'yachtbeach': '/collections/all-yachtbeach',
      'yacht beach': '/collections/all-yachtbeach',
      'torque': '/collections/torque-jetboards',
      'jetwave': '/pages/jetwave',
      'jet wave': '/pages/jetwave',
      'kaabo': '/pages/kaabo',
      'nanrobot': '/pages/nanrobot',
      'nan robot': '/pages/nanrobot',
      'hydrix': '/pages/hydrix'
    };

    if (directRoutes[term]) {
      event.preventDefault();
      window.location.href = directRoutes[term];
      return;
    }

    const singleResultContainer =
      this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');

    if (
      singleResultContainer instanceof HTMLElement &&
      singleResultContainer.dataset.singleResultUrl
    ) {
      event.preventDefault();
      window.location.href = singleResultContainer.dataset.singleResultUrl;
      return;
    }

    if (this.#currentIndex >= 0) {
      const currentLink = this.#currentItem?.querySelector('a');

      if (currentLink) {
        event.preventDefault();
        currentLink.click();
        return;
      }
    }

    const firstResult =
      this.refs.predictiveSearchResults.querySelector(
        '.predictive-search-brand-result__link, [ref="resultsItems[]"] a'
      );

    if (firstResult) {
      event.preventDefault();
      firstResult.click();
      return;
    }

    /*
     * Important:
     * Even with no result, do NOT submit to /search.
     */
    event.preventDefault();
    return;
  }

  if (
    !this.#allResultsItems?.length ||
    event.key === 'ArrowLeft' ||
    event.key === 'ArrowRight'
  ) {
    return;
  }

  const currentIndex = this.#currentIndex;
  const totalItems = this.#allResultsItems.length;

  switch (event.key) {
    case 'ArrowDown':
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex =
        currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
      break;

    case 'Tab':
      this.#isKeyboardNavigation = true;
      event.preventDefault();

      if (event.shiftKey) {
        this.#currentIndex =
          currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
      } else {
        this.#currentIndex =
          currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
      }
      break;

    case 'ArrowUp':
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex =
        currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
      break;
  }
};

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
url.searchParams.set('q', searchTerm);
url.searchParams.set('resources[type]', 'product');
url.searchParams.set(
  'resources[options][fields]',
  'title,vendor,product_type'
);
url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
  const { predictiveSearchResults, searchInput } = this.refs;

  // Cancel any search request that is still loading.
  this.#createAbortController();

  this.#currentIndex = -1;

  searchInput.value = '';
  searchInput.setAttribute('aria-expanded', 'false');

  this.#hideResetButton();

  // Remove all existing predictive results immediately.
  predictiveSearchResults.innerHTML = '';

  this.#resetScrollPositions();

  searchInput.focus();
};
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
